import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Heading,
  Badge,
  IconButton,
  Tooltip,
  useToast,
  Select,
  HStack,
  Text,
} from '@chakra-ui/react';
import { Link as RouterLink } from 'react-router-dom';
import { FiUser, FiCreditCard } from 'react-icons/fi';
import DataTable, { Column } from '@/components/common/DataTable';
import apiClient from '@/services/letsparkyClient';
import type { Device, User, Parking, Tenant } from '@letsparky/api-v2-client';
import { useAuthStore } from '@/stores/authStore';
import { useTranslation } from 'react-i18next';
import { durationToString } from '@/utils/utils';

interface ParkingView {
  id: string;
  deviceName: string;
  qrCode: string;
  entryTime?: number;
  exitTime?: number;
  duration: string;
  status: string;
  tenantName: string;
  paymentId?: string;
  user?: { id: string; name: string; email: string };
}

const Parkings: React.FC = () => {
  const toast = useToast();
  const { user, token } = useAuthStore();
  const { t } = useTranslation();
  const isSystem = user?.role === 'SYSTEM';

  // Data states
  const [parkings, setParkings] = useState<ParkingView[]>([]);
  const [tenants, setTenants] = useState<Tenant[]>([]);

  // UI states
  const [isLoading, setIsLoading] = useState(false);
  const [totalItems, setTotalItems] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [statusFilter, setStatusFilter] = useState('');
  const [tenantFilter, setTenantFilter] = useState('');

  // Fetch tenants for filter dropdown (system users only)
  useEffect(() => {
    if (!isSystem || !token) return;

    const fetchTenants = async () => {
      try {
        apiClient.client.setToken(token);
        const response = await apiClient.tenants.list({ limit: 1000 });
        if (response?.data?.data) {
          setTenants(response.data.data);
        }
      } catch (error) {
        console.error('Error fetching tenants:', error);
      }
    };

    fetchTenants();
  }, [isSystem, token]);

  // Main data fetching
  const fetchParkings = useCallback(async () => {
    if (!token) return;
    
    setIsLoading(true);
    try {
      apiClient.client.setToken(token);

      const params: Record<string, string> = {};
      
      if (!isSystem && user?.tenantId) {
        params.tenant_id = user.tenantId;
      }
      if (searchQuery.trim()) {
        params.search = searchQuery.trim();
      }
      if (statusFilter) {
        params.status = statusFilter;
      }
      if (tenantFilter && isSystem) {
        params.tenant_id = tenantFilter;
      }
      if (sortBy) {
        params.sort_by = sortBy;
        params.sort_direction = sortDirection;
      }

      // Fetch parkings
      const parkingsResponse = await apiClient.parkings.listPaginated(
        { page: currentPage, limit: pageSize },
        params
      );

      // Get unique device and user IDs
      const deviceIds = new Set(parkingsResponse.data.map(p => p.device_id).filter(Boolean));
      const userIds = new Set(parkingsResponse.data.map(p => p.user_id).filter(Boolean));

      // Fetch related data in parallel
      const [devicesRes, usersRes, tenantsRes] = await Promise.all([
        deviceIds.size > 0
          ? apiClient.devices.listPaginated({ page: 1, limit: 2000 }, params.tenant_id ? { tenant_id: params.tenant_id } : {})
          : Promise.resolve(null),
        userIds.size > 0
          ? apiClient.users.listPaginated({ page: 1, limit: 2000 }, params.tenant_id ? { tenant_id: params.tenant_id } : {})
          : Promise.resolve(null),
        isSystem ? apiClient.tenants.list({ limit: 1000 }) : Promise.resolve(null),
      ]);

      // Create lookup maps
      const deviceMap = new Map<string, Device>((devicesRes?.data || []).map((d: Device) => [d.id, d]));
      const userMap = new Map<string, User>((usersRes?.data || []).map((u: User) => [u.id, u]));
      const tenantList: Tenant[] = (tenantsRes as any)?.data?.data || [];
      const tenantMap = new Map<string, Tenant>(tenantList.map((tenant) => [tenant.id, tenant]));

      // Transform to view model
      const transformed: ParkingView[] = parkingsResponse.data.map((parking: Parking) => {
        const device = deviceMap.get(parking.device_id);
        const parkingUser = userMap.get(parking.user_id || '');
        const tenant = device?.tenant_id ? tenantMap.get(device.tenant_id) : undefined;

        return {
          id: parking.id,
          deviceName: device?.nick_name || t('common.unknown'),
          qrCode: device?.serial_no || '-',
          entryTime: parking.start,
          exitTime: parking.end,
          duration: parking.end 
            ? durationToString((parking.end - parking.start!) / 1000) 
            : durationToString((Date.now() - parking.start!) / 1000),
          status: parking.status,
          tenantName: tenant?.name || '-',
          paymentId: (parking as Parking & { payment_id?: string }).payment_id,
          user: parkingUser ? {
            id: parkingUser.id,
            name: parkingUser.name || '',
            email: parkingUser.email || '',
          } : undefined,
        };
      });

      setParkings(transformed);
      setTotalItems(parkingsResponse.pagination.total);
    } catch (error) {
      toast({
        title: t('common.error'),
        description: error instanceof Error ? error.message : 'Unknown error',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  }, [token, currentPage, pageSize, searchQuery, statusFilter, tenantFilter, sortBy, sortDirection, isSystem, user?.tenantId, t, toast]);

  useEffect(() => {
    fetchParkings();
  }, [fetchParkings]);

  const handleSearch = (query: string) => {
    setSearchQuery(query);
    setCurrentPage(1);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handlePageSizeChange = (size: number) => {
    setPageSize(size);
    setCurrentPage(1);
  };

  const handleSort = (newSortBy: string, newSortDirection: 'asc' | 'desc') => {
    setSortBy(newSortBy);
    setSortDirection(newSortDirection);
    setCurrentPage(1);
  };

  const getStatusColor = (status: string, hasPaid: boolean) => {
    if (hasPaid) return 'green';
    switch (status.toLowerCase()) {
      case 'active': return 'blue';
      case 'completed': return 'orange';
      case 'canceled': return 'red';
      default: return 'gray';
    }
  };

  const columns: Column<ParkingView>[] = [
    {
      header: t('parkings.columns.deviceName'),
      accessor: 'deviceName',
      sortable: true,
      sortKey: 'device_id',
    },
    {
      header: t('parkings.columns.qrCode'),
      accessor: 'qrCode',
    },
    {
      header: t('parkings.columns.entryTime'),
      accessor: (p) => p.entryTime 
        ? new Date(p.entryTime).toLocaleString('en-US', { hour12: false }) 
        : '-',
      sortable: true,
      sortKey: 'start',
    },
    {
      header: t('parkings.columns.exitTime'),
      accessor: (p) => p.exitTime 
        ? new Date(p.exitTime).toLocaleString('en-US', { hour12: false }) 
        : '-',
      sortable: true,
      sortKey: 'end',
    },
    {
      header: t('parkings.columns.duration'),
      accessor: 'duration',
    },
    {
      header: t('parkings.columns.status'),
      accessor: (p) => (
        <Badge colorScheme={getStatusColor(p.status, !!p.paymentId)}>
          {p.paymentId ? t('parkings.status.paid') : t(`parkings.status.${p.status.toLowerCase()}`)}
        </Badge>
      ),
      sortable: true,
      sortKey: 'status',
    },
    ...(isSystem ? [{
      header: t('parkings.columns.tenant'),
      accessor: 'tenantName' as keyof ParkingView,
    }] : []),
  ];

  const actionButtons = (parking: ParkingView) => (
    <>
      {parking.user && (
        <Tooltip label={parking.user.name || parking.user.email}>
          <IconButton
            as={RouterLink}
            to={`/users?search=${encodeURIComponent(parking.user.email || parking.user.name)}`}
            aria-label={t('parkings.viewUser')}
            icon={<FiUser />}
            size="sm"
            variant="ghost"
          />
        </Tooltip>
      )}
      {parking.paymentId && (
        <Tooltip label={t('parkings.viewPayment')}>
          <IconButton
            as={RouterLink}
            to={`/payments?search=${encodeURIComponent(parking.paymentId)}`}
            aria-label={t('parkings.viewPayment')}
            icon={<FiCreditCard />}
            size="sm"
            variant="ghost"
            colorScheme="green"
          />
        </Tooltip>
      )}
    </>
  );

  return (
    <Box>
      <Heading size="lg" mb={6}>{t('parkings.title')}</Heading>

      {/* Filters */}
      <HStack mb={4} spacing={4}>
        <HStack>
          <Text fontSize="sm" whiteSpace="nowrap">{t('common.status')}:</Text>
          <Select
            size="sm"
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setCurrentPage(1);
            }}
            minW="120px"
          >
            <option value="">{t('common.all')}</option>
            <option value="ACTIVE">{t('parkings.status.active')}</option>
            <option value="COMPLETED">{t('parkings.status.completed')}</option>
            <option value="CANCELED">{t('parkings.status.canceled')}</option>
          </Select>
        </HStack>

        {isSystem && tenants.length > 0 && (
          <HStack>
            <Text fontSize="sm" whiteSpace="nowrap">{t('parkings.columns.tenant')}:</Text>
            <Select
              size="sm"
              value={tenantFilter}
              onChange={(e) => {
                setTenantFilter(e.target.value);
                setCurrentPage(1);
              }}
              minW="150px"
            >
              <option value="">{t('common.all')}</option>
              {tenants.map((tenant) => (
                <option key={tenant.id} value={tenant.id}>
                  {tenant.name}
                </option>
              ))}
            </Select>
          </HStack>
        )}
      </HStack>

      <DataTable
        columns={columns}
        data={parkings}
        keyField="id"
        isLoading={isLoading}
        totalItems={totalItems}
        currentPage={currentPage}
        pageSize={pageSize}
        sort_by={sortBy}
        sort_direction={sortDirection}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        onSearch={handleSearch}
        onSort={handleSort}
        actionButtons={actionButtons}
      />
    </Box>
  );
};

export default Parkings;
